
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";

import {
    FiChevronLeft,
    FiChevronRight,
    FiCalendar,
} from "react-icons/fi";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
} from "../../Components/Styles/PageStyles";
import Config from "../../Config";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import Loading from "../../Components/Loading";
import { truncateText } from "../../helpers";

const COLORS = {
    occupied: "bg-emerald-400",
    vacant: "bg-slate-200",
    checkin: "bg-blue-500",
    checkout: "bg-rose-400",
    blocked: "bg-amber-300",
    // agreementEnd: "bg-violet-500",
    agreementExpired: "bg-red-300",
};

const STATUS_LABELS = {
    occupied: "Occupied",
    vacant: "Vacant",
    checkin: "Move-in",
    checkout: "Move-out",
    // agreementEnd: "Agreement End",
    agreementExpired: "Renewal Pending",
    //blocked: "Maintenance / Blocked",
};

const isAgreementEnd = (item) =>
    item?.isEvicted === 0 && item?.status !== 4;

const isAgreementExpiredPending = (item) => {
    if (
        item?.isEvicted !== 0 ||
        item?.status === 4 ||
        !item?.to
    ) {
        return false;
    }

    const [year, month, day] = item.to.split("-").map(Number);
    const endDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate < today;
};

const isAgreementActiveButNotRenewed = (item) => {
    return (
        item?.isEvicted === 0 &&
        item?.status !== 4 &&
        item?.to &&
        !isAgreementExpiredPending(item)
    );
};

const isRenewalPending = (item) =>
    item?.isEvicted === 0 &&
    item?.status !== 4 &&
    Boolean(item?.to);

const isExpiredOnDateOld = (item, calendarDate) => {
    if (
        item?.isEvicted !== 0 ||
        item?.status === 4 ||
        !item?.to
    ) {
        return false;
    }

    const [year, month, day] = item.to.split("-").map(Number);
    const agreementEndDate = new Date(year, month - 1, day);

    return calendarDate > agreementEndDate;
};

const isExpiredOnDate = (item, calendarDate) => {
    if (
        item?.isEvicted !== 0 ||
        item?.status === 4 ||
        !item?.to
    ) {
        return false;
    }

    const [year, month, day] = item.to.split("-").map(Number);
    const agreementEndDate = new Date(year, month - 1, day);

    return calendarDate > agreementEndDate;
};

const getMonthDays = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const formatMonth = (date) =>
    date.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
    });

const formatDate = (date) =>
    date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const getDayName = (date) =>
    date.toLocaleDateString("en-IN", { weekday: "short" });

function getDayStatus(bed, day, daysInMonth) {
    if (bed.blocked?.some((period) => day >= period.from && day <= period.to)) {
        return "blocked";
    }

    const stay = bed.stays?.find(
        (period) => day >= period.from && day <= period.to
    );

    if (stay) {
        if (day === stay.from) return "checkin";
        if (day === stay.to) return "checkout";
        return "occupied";
    }

    return "vacant";
}

function getBedMetricsOld(bed, daysInMonth) {
    let occupiedDays = 0;
    let vacantDays = 0;
    let blockedDays = 0;

    for (let day = 1; day <= daysInMonth; day++) {
        const status = getDayStatus(bed, day, daysInMonth);

        if (status === "occupied" || status === "checkin" || status === "checkout") {
            occupiedDays++;
        } else if (status === "vacant") {
            vacantDays++;
        } else {
            blockedDays++;
        }
    }

    return {
        occupiedDays,
        vacantDays,
        blockedDays,
        occupancy: daysInMonth
            ? Math.round((occupiedDays / daysInMonth) * 100)
            : 0,
    };
}

const parseLocalDate = (value) => {
    if (value instanceof Date) return value;

    const [year, month, day] = String(value).split("-").map(Number);
    return new Date(year, month - 1, day);
};

const isAgreementExpired = (item) => {
    if (
        item?.isEvicted !== 0 ||
        item?.status === 4 ||
        !item?.to
    ) {
        return false;
    }

    const endDate = parseLocalDate(item.to);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate < today;
};

function getBedMetrics(bed, startDate, endDate) {
    let occupiedDays = 0;
    let vacantDays = 0;
    let blockedDays = 0;

    const stays = bed.stays || [];
    const blockedPeriods = bed.blocked || [];

    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
        const dayKey = [
            current.getFullYear(),
            String(current.getMonth() + 1).padStart(2, "0"),
            String(current.getDate()).padStart(2, "0"),
        ].join("-");

        const isBlocked = blockedPeriods.some(
            (period) =>
                dayKey >= period.from && dayKey <= period.to
        );

        const isOccupiedOld = stays.some(
            (stay) =>
                dayKey >= stay.from && dayKey <= stay.to
        );

        const isOccupied = stays.some((stay) => {
            // Normal agreement period
            if (dayKey >= stay.from && dayKey <= stay.to) {
                return true;
            }

            // After agreement expiry, count as occupied while
            // the tenant has not moved out and renewal is pending.
            if (
                dayKey > stay.to &&
                isAgreementExpired(stay)
            ) {
                // A subsequent tenant's stay supersedes this agreement.
                const nextTenantStarted = stays.some(
                    (next) =>
                        next.from > stay.from &&
                        next.from <= dayKey &&
                        next.isEvicted === 0 &&
                        next.status !== 4
                );

                return !nextTenantStarted;
            }

            return false;
        });

        if (isBlocked) {
            blockedDays++;
        } else if (isOccupied) {
            occupiedDays++;
        } else {
            vacantDays++;
        }

        current.setDate(current.getDate() + 1);
    }

    const availableDays = occupiedDays + vacantDays;

    return {
        occupiedDays,
        vacantDays,
        blockedDays,
        occupancy: availableDays
            ? Math.round((occupiedDays / availableDays) * 100)
            : 0,
    };
}

function getBedName(bed) {
    return `Room ${bed.room} - ${bed.bed}`;
}

function BedsCalendar() {
    const [rawBeds, setRawBeds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [selectedFloor, setSelectedFloor] = useState("all");
    const [floors, setFloors] = useState([
        { id: "all", label: "All Rooms" },
    ]);
    const [selectedBedId, setSelectedBedId] = useState(202);
    const [month, setMonth] = useState(
        () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    );
    const [timelineView, setTimelineView] = useState("month");
    const user = useSelector((state) => state.UserReducer.user);
    const properties = useSelector((state) => state.PropertyReducer.property);
    const filteredProperties = properties?.filter(prop => prop.id !== 0) || [];
    //console.log (filteredProperties);
    const property = filteredProperties?.[0];

    const daysInMonth = getMonthDays(month);

    const timelinePeriods = timelineView === "year"
        ? Array.from({ length: 12 }, (_, index) => ({
            value: index,
            label: new Date(month.getFullYear(), index, 1)
                .toLocaleDateString("en-IN", { month: "short" }),
        }))
        : Array.from({ length: daysInMonth }, (_, index) => ({
            value: index + 1,
            label: String(index + 1),
        }));

    // const selectedProperty = PROPERTIES.find(
    //     (property) => property.id === selectedPropertyId
    // );

    const [selectedProperty, setSelectedProperty] = useState(property);
    const [selectedPropertyId, setSelectedPropertyId] = useState(property?.id);


    useEffect(() => {
        let isActive = true;

        const fetchOccupancyCalendar = async () => {
            setLoading(true);
            setApiError("");

            const formatApiDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
            };

            const startDate = new Date(
                month.getFullYear(),
                timelineView === "year" ? 0 : month.getMonth(),
                1
            );

            const endDate = new Date(
                month.getFullYear(),
                timelineView === "year" ? 12 : month.getMonth() + 1,
                0
            );

            try {

                const params = new URLSearchParams({
                    propId: selectedPropertyId,
                    startDate: formatApiDate(startDate),
                    endDate: formatApiDate(endDate),
                });

                const response = await axios.get(
                    `${Config.apiUrl}/property/list/occupancy/calendar?${params.toString()}`,
                    {
                        headers: {
                            authorization: `Bearer ${user.token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );


                if (!isActive) return;

                // API response: { msg, isSuccess, data: [{ bedId, bedStatus, floor, room, stays }] }
                const payload = response?.data?.data ?? response?.data;
                if (response?.data?.isSuccess === false) {
                    throw new Error(response?.data?.msg || "Unable to load occupancy calendar.");
                }
                const fetchedBeds = Array.isArray(payload)
                    ? payload
                    : payload?.beds ?? payload?.items ?? payload?.rows ?? [];

                if (!Array.isArray(fetchedBeds)) {
                    throw new Error("Unexpected occupancy calendar API response.");
                }

                // The API does not return a bed label, so number beds within each room.
                const roomBedCounts = {};
                const normalizedBeds = fetchedBeds.map((item) => {
                    const roomKey = `${item.floor ?? ""}-${item.room ?? ""}`;
                    roomBedCounts[roomKey] = (roomBedCounts[roomKey] || 0) + 1;

                    return {
                        ...item,
                        id: item.bedId ?? item.id,
                        //floor: String(item.floor ?? ""),
                        floor: String(item.floor ?? item.flat ?? ""),
                        floorLabel: item.floor != null
                            ? `Floor ${item.floor}`
                            : `Flat ${item.flat ?? "-"}`,
                        room: String(item.room ?? ""),
                        bed: item.bed ?? `Bed ${roomBedCounts[roomKey]}`,
                        bedStatus: item.bedStatus,
                        stays: (Array.isArray(item.stays) ? item.stays : []).map((stay) => ({
                            ...stay,
                            from: stay.from,
                            to: stay.to,
                            tenant: stay.tenant ?? "Tenant",
                        })),
                        blocked: Array.isArray(item.blocked) ? item.blocked : [],
                    };
                });

                setRawBeds(normalizedBeds);

                const uniqueFloors = [
                    ...new Map(
                        normalizedBeds.map((bed) => [
                            bed.floor,
                            {
                                id: bed.floor,
                                label: bed.floorLabel,
                            },
                        ])
                    ).values(),
                ];

                setFloors([
                    { id: "all", label: "All Rooms" },
                    ...uniqueFloors.sort((a, b) =>
                        a.id.localeCompare(b.id, undefined, { numeric: true })
                    ),
                ]);

                setSelectedFloor("all");

            } catch (error) {
                if (!isActive) return;
                setRawBeds([]);
                setApiError(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to load occupancy calendar."
                );
            } finally {
                if (isActive) setLoading(false);
            }
        };

        fetchOccupancyCalendar();

        return () => {
            isActive = false;
        };
    }, [selectedPropertyId, month, timelineView, user?.token]);

    // const beds = useMemo(() => {
    //     return rawBeds.map((bed) => ({
    //         ...bed,
    //         metrics: getBedMetrics(bed, daysInMonth),
    //     }));
    // }, [rawBeds, daysInMonth]);

    const metricsStartDate = new Date(
        month.getFullYear(),
        timelineView === "year" ? 0 : month.getMonth(),
        1
    );

    const metricsEndDate = new Date(
        month.getFullYear(),
        timelineView === "year" ? 12 : month.getMonth() + 1,
        0
    );

    const beds = useMemo(() => {
        return rawBeds.map((bed) => ({
            ...bed,
            metrics: getBedMetrics(
                bed,
                metricsStartDate,
                metricsEndDate
            ),
        }));
    }, [rawBeds, metricsStartDate.getTime(), metricsEndDate.getTime()]);

    const filteredBeds = useMemo(() => {
        return beds.filter(
            (bed) => selectedFloor === "all" || bed.floor === selectedFloor
        );
    }, [beds, selectedFloor]);

    const selectedBed =
        filteredBeds.find((bed) => bed.id === selectedBedId) ||
        beds.find((bed) => bed.id === selectedBedId) ||
        filteredBeds[0] ||
        null;

    const summary = useMemo(() => {
        const occupiedDays = beds.reduce(
            (total, bed) => total + bed.metrics.occupiedDays,
            0
        );

        const vacantDays = beds.reduce(
            (total, bed) => total + bed.metrics.vacantDays,
            0
        );

        const blockedDays = beds.reduce(
            (total, bed) => total + bed.metrics.blockedDays,
            0
        );

        const totalDays = occupiedDays + vacantDays + blockedDays;
        const availableDays = occupiedDays + vacantDays;

        return {
            totalBeds: beds.length,
            occupiedDays,
            vacantDays,
            blockedDays,
            occupancy: availableDays
                ? Math.round((occupiedDays / availableDays) * 100)
                : 0,
            occupiedBeds: beds.length
                ? Math.round(
                    beds.reduce(
                        (total, bed) =>
                            total + bed.metrics.occupancy,
                        0
                    ) / beds.length
                )
                : 0,
            vacantBeds: beds.length
                ? Math.round(
                    (vacantDays / (timelineView === "year"
                        ? (new Date(month.getFullYear(), 12, 0).getDate() +
                            new Date(month.getFullYear(), 11, 31).getDate() +
                            new Date(month.getFullYear(), 10, 30).getDate()) * beds.length
                        : daysInMonth * beds.length)) * 100
                )
                : 0,
        };
    }, [beds, daysInMonth, month, timelineView]);

    const handleMonthChange = (offset) => {
        setMonth((current) =>
            timelineView === "year"
                ? new Date(current.getFullYear() + offset, 0, 1)
                : new Date(current.getFullYear(), current.getMonth() + offset, 1)
        );
    };

    const selectedStays = useMemo(() => {
        if (!selectedBed) return [];

        const events = [];

        selectedBed.stays.forEach((stay) => {
            events.push({
                status: "Occupied",
                from: stay.from,
                to: stay.to,
                tenant: stay.tenant,
                days: stay.to - stay.from + 1,
            });
        });

        // Add vacant gaps between occupancy periods.
        let cursor = 1;
        const stays = [...selectedBed.stays].sort(
            (a, b) => a.from - b.from
        );

        stays.forEach((stay) => {
            if (cursor < stay.from) {
                events.push({
                    status: "Vacant",
                    from: cursor,
                    to: stay.from - 1,
                    tenant: "-",
                    days: stay.from - cursor,
                });
            }

            cursor = Math.max(cursor, stay.to + 1);
        });

        if (cursor <= daysInMonth) {
            events.push({
                status: "Vacant",
                from: cursor,
                to: daysInMonth,
                tenant: "-",
                days: daysInMonth - cursor + 1,
            });
        }

        return events
            .map((event) => ({
                ...event,
                fromDate: new Date(
                    month.getFullYear(),
                    month.getMonth(),
                    event.from
                ),
                toDate: new Date(
                    month.getFullYear(),
                    month.getMonth(),
                    event.to
                ),
            }))
            .sort((a, b) => a.from - b.from);
    }, [selectedBed, daysInMonth, month]);

    const handlePropertySelect = (property) => {
        setSelectedProperty(property);
        setSelectedPropertyId(property?.id ?? property?.propId);
    };

    return (
        <BoxContainer>

            <BoxHead className="flex flex-wrap items-center justify-between gap-3">
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Bed Calendar
                    <Underline />
                </BoxTitle>

                <div className="flex flex-wrap items-center gap-2">

                    <PropertiesFilterDropdown
                        properties={filteredProperties}
                        selectedProperty={selectedProperty}
                        onPropertySelect={handlePropertySelect}
                        placeholder="All Properties"
                        className="w-full md:w-[200px]"
                        showIcon={true}
                    />

                    {/* <div className="flex h-10 items-center rounded-md border border-gray-200 bg-white p-1">
                        {["month", "year"].map((view) => (
                            <button
                                key={view}
                                type="button"
                                onClick={() => setTimelineView(view)}
                                className={`rounded px-3 py-1.5 text-sm font-medium capitalize ${timelineView === view
                                    ? "bg-cyan-50 text-primary-color"
                                    : "text-gray-600 hover:bg-gray-50"
                                    }`}
                            >
                                {view}
                            </button>
                        ))}
                    </div> */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-white rounded-md p-1 h-10">
                            <SegmentButton
                                active={timelineView === "month"}
                                onClick={() => setTimelineView("month")}
                            >
                                Month
                            </SegmentButton>

                            <SegmentButton
                                active={timelineView === "year"}
                                onClick={() => setTimelineView("year")}
                            >
                                Year
                            </SegmentButton>
                        </div>
                    </div>

                    {/* Month navigation */}
                    <button
                        type="button"
                        onClick={() => handleMonthChange(-1)}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        aria-label={`Previous ${timelineView}`}
                    >
                        <FiChevronLeft />
                    </button>

                    <div className="flex h-10 min-w-[160px] items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700">
                        <FiCalendar className="text-gray-400" />
                        {timelineView === "year"
                            ? month.getFullYear()
                            : formatMonth(month)}
                    </div>

                    <button
                        type="button"
                        onClick={() => handleMonthChange(1)}
                        className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        aria-label={`Next ${timelineView}`}
                    >
                        <FiChevronRight />
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            const currentDate = new Date();

                            setMonth(
                                timelineView === "year"
                                    ? new Date(currentDate.getFullYear(), 0, 1)
                                    : new Date(
                                        currentDate.getFullYear(),
                                        currentDate.getMonth(),
                                        1
                                    )
                            );
                        }}
                        className="h-10 rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                        Today
                    </button>
                </div>
            </BoxHead>

            <div className="min-w-0 w-full">
                {/* Floor tabs */}
                <div className="my-5 flex items-center justify-between rounded-md border-gray-200 border-b bg-white">
                    <div className="flex min-w-0 overflow-x-auto">
                        {floors.map((floor) => (
                            <button
                                key={floor.id}
                                type="button"
                                onClick={() => setSelectedFloor(floor.id)}
                                className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${selectedFloor === floor.id
                                    ? "border-primary-color bg-cyan-50 text-primary-color"
                                    : "border-transparent text-gray-600 hover:bg-gray-50"
                                    }`}
                            >
                                {floor.label}
                            </button>
                        ))}
                    </div>

                    {/* <button
                            type="button"
                            onClick={() => window.print()}
                            className="mr-2 flex shrink-0 items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                        >
                            <FiDownload />
                            Export
                        </button> */}
                </div>

                {apiError && (
                    <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {apiError}
                    </div>
                )}

                {/* Occupancy timeline */}
                <div className="w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                        <div>
                            <h3 className="text-base font-semibold text-gray-800">
                                {selectedProperty?.name} · {
                                    timelineView === "year"
                                        ? month.getFullYear()
                                        : formatMonth(month)
                                }
                            </h3>
                            <p className="mt-1 text-xs text-gray-400">
                                Bed Occupancy Timeline
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            {Object.entries(STATUS_LABELS).map(
                                ([status, label]) => (
                                    <div
                                        key={status}
                                        className="flex items-center gap-1.5 text-xs text-gray-500"
                                    >
                                        <span
                                            className={`h-2.5 w-2.5 rounded-sm ${COLORS[status]}`}
                                        />
                                        {label}
                                    </div>
                                )
                            )}
                        </div>
                    </div>

                    <div className="w-full min-w-0 overflow-x-auto">
                        <table
                            className={`border-collapse ${timelineView === "year"
                                ? "w-full"
                                : "min-w-[1100px] w-full"
                                } text-xs`}
                        >
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="sticky left-0 z-10 w-[100px] min-w-[100px] border-b border-r border-gray-200 bg-gray-50 px-2 py-3 text-left font-semibold text-gray-600">
                                        Room / Bed
                                    </th>

                                    {timelinePeriods.map((period) => {
                                        const date = timelineView === "year"
                                            ? new Date(month.getFullYear(), period.value, 1)
                                            : new Date(
                                                month.getFullYear(),
                                                month.getMonth(),
                                                period.value
                                            );

                                        return (
                                            <th
                                                key={period.value}
                                                // className={`border-b border-gray-200 px-0.5 py-2 text-center font-medium text-gray-500 ${timelineView === "year"
                                                //     ? "min-auto"
                                                //     : "min-w-[29px] w-[29px]"
                                                //     }`}
                                                className={`border-b border-gray-200 px-0 py-2 text-center font-medium text-gray-500 ${timelineView === "year"
                                                    ? "w-auto whitespace-nowrap"
                                                    : "min-w-[29px] w-[29px]"
                                                    }`}
                                            >
                                                {timelineView === "month" && (
                                                    <div className="mb-1 text-[9px]">
                                                        {getDayName(date)}
                                                    </div>
                                                )}
                                                <div>{period.label}</div>
                                            </th>
                                        );
                                    })}

                                    <th className="w-[68px] min-w-[68px] border-b border-l border-gray-200 px-1 py-2 text-center text-gray-500 whitespace-nowrap">
                                        Occupied
                                    </th>

                                    <th className="w-[60px] min-w-[60px] border-b border-gray-200 px-1 py-2 text-center text-gray-500 whitespace-nowrap">
                                        Vacant
                                    </th>

                                    <th className="w-[76px] min-w-[76px] border-b border-gray-200 px-1 py-2 text-center text-gray-500 whitespace-nowrap">
                                        Occupancy %
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={timelineView === "month" ? timelinePeriods.length + 2 : timelinePeriods.length} className="py-2 text-center text-sm text-gray-500">
                                            <Loading />
                                        </td>
                                    </tr>
                                )}
                                {!loading && filteredBeds.map((bed, bedIndex) => {
                                    const selected =
                                        selectedBedId === bed.id;

                                    return (
                                        <tr
                                            key={bed.id}
                                            onClick={() =>
                                                setSelectedBedId(bed.id)
                                            }
                                            className={`cursor-pointer border-b border-gray-100 transition-colors ${selected
                                                ? "bg-blue-50/70"
                                                : "hover:bg-gray-50"
                                                }`}
                                        >
                                            <td
                                                className={`sticky left-0 z-[1] border-r border-gray-100 px-3 py-3 font-medium text-gray-700 ${selected
                                                    ? "bg-blue-50"
                                                    : "bg-white"
                                                    }`}
                                            >
                                                <div className="whitespace-nowrap">
                                                    {bed.room} - {bed.bed}
                                                </div>
                                                <div className="mt-1 text-[10px] font-normal text-gray-400">
                                                    {bed.floorLabel}
                                                </div>
                                            </td>

                                            {timelinePeriods.map((period) => {

                                                const periodStart = timelineView === "year"
                                                    ? new Date(month.getFullYear(), period.value, 1)
                                                    : new Date(month.getFullYear(), month.getMonth(), period.value);

                                                const periodEnd = timelineView === "year"
                                                    ? new Date(month.getFullYear(), period.value + 1, 0)
                                                    : periodStart;

                                                const date = periodStart;

                                                const toDate = (value) => {
                                                    const [year, month, day] = value.split("-").map(Number);
                                                    return new Date(year, month - 1, day);
                                                };

                                                const periodStays = bed.stays.filter((item) => {
                                                    const stayFrom = toDate(item.from);
                                                    const stayTo = toDate(item.to);

                                                    return stayFrom <= periodEnd && stayTo >= periodStart;
                                                });

                                                const dateKey = (date) =>
                                                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

                                                // const stay = periodStays.find(
                                                //     (item) =>
                                                //         toDate(item.from) <= periodStart &&
                                                //         toDate(item.to) >= periodStart
                                                // );
                                                const stay = periodStays
                                                    .filter(
                                                        (item) =>
                                                            toDate(item.from) <= periodStart &&
                                                            toDate(item.to) >= periodStart
                                                    )
                                                    .sort((a, b) => {
                                                        // If multiple tenants share the same date,
                                                        // prioritize the tenant who moved in that day.
                                                        const aStartsToday = a.from === dateKey(periodStart);
                                                        const bStartsToday = b.from === dateKey(periodStart);

                                                        if (aStartsToday && !bStartsToday) return -1;
                                                        if (!aStartsToday && bStartsToday) return 1;

                                                        return toDate(b.from) - toDate(a.from);
                                                    })[0];

                                                // Find a previous agreement that expired but is still pending renewal.

                                                const expiredStay = bed.stays
                                                    .filter((item) => {
                                                        const agreementEnd = toDate(item.to);

                                                        return (
                                                            toDate(item.from) <= periodStart &&
                                                            periodStart > agreementEnd &&
                                                            isAgreementExpired(item) &&
                                                            !bed.stays.some(
                                                                (next) =>
                                                                    toDate(next.from) > toDate(item.from) &&
                                                                    toDate(next.from) <= periodStart &&
                                                                    next.isEvicted === 0 &&
                                                                    next.status !== 4
                                                            )
                                                        );
                                                    })
                                                    .sort((a, b) => toDate(b.to) - toDate(a.to))[0];


                                                const statusTrash = bed.blocked?.some((item) => {
                                                    const blockedFrom = toDate(item.from);
                                                    const blockedTo = toDate(item.to);

                                                    return blockedFrom <= periodEnd && blockedTo >= periodStart;
                                                })
                                                    ? "blocked"
                                                    : stay
                                                        ? (
                                                            isExpiredOnDate(stay, periodStart) ||
                                                            (
                                                                stay.isEvicted === 0 &&
                                                                stay.status !== 4 &&
                                                                stay.to === dateKey(periodStart)
                                                            )
                                                        )
                                                            ? "agreementExpired"
                                                            : stay.from === dateKey(periodStart)
                                                                ? "checkin"
                                                                : stay.to === dateKey(periodStart)
                                                                    ? "checkout"
                                                                    : "occupied"
                                                        : expiredStay
                                                            ? "agreementExpired"
                                                            : "vacant";

                                                const startKey = dateKey(periodStart);

                                                const isBlocked = bed.blocked?.some((item) => {
                                                    const blockedFrom = toDate(item.from);
                                                    const blockedTo = toDate(item.to);
                                                    return blockedFrom <= periodEnd && blockedTo >= periodStart;
                                                });

                                                let status = "vacant";

                                                if (isBlocked) {
                                                    status = "blocked";
                                                } else if (stay) {
                                                    if (stay.from === startKey) {
                                                        status = "checkin";
                                                    } else if (stay.to === startKey) {
                                                        // Last day: red only if the agreement has really expired (end date is before today).
                                                        status = isAgreementExpired(stay) ? "agreementExpired" : "checkout";
                                                    } else {
                                                        status = "occupied";
                                                    }
                                                } else if (expiredStay) {
                                                    status = "agreementExpired";
                                                }

                                                {/* This is for Year */ }

                                                const tenantColors = [
                                                    "bg-emerald-500",
                                                    "bg-blue-500",
                                                    "bg-violet-500",
                                                    "bg-teal-500",
                                                    "bg-amber-500",
                                                ];

                                                const segmentOld = periodStays
                                                    .map((item) => {
                                                        const monthStart = periodStart.getTime();
                                                        const monthEnd = periodEnd.getTime();

                                                        const stayStart = Math.max(
                                                            toDate(item.from).getTime(),
                                                            monthStart
                                                        );

                                                        const stayEnd = Math.min(
                                                            toDate(item.to).getTime(),
                                                            monthEnd
                                                        );

                                                        const totalDays =
                                                            Math.round((monthEnd - monthStart) / 86400000) + 1;

                                                        const startDay =
                                                            Math.round((stayStart - monthStart) / 86400000);

                                                        const duration =
                                                            Math.round((stayEnd - stayStart) / 86400000) + 1;

                                                        return {
                                                            item,
                                                            left: (startDay / totalDays) * 100,
                                                            width: (duration / totalDays) * 100,
                                                            startDay,
                                                            endDay: startDay + duration - 1,
                                                        };
                                                    })
                                                    .sort((a, b) => a.startDay - b.startDay);


                                                const yearStays = timelineView === "year"
                                                    ? bed.stays.filter((item) => {
                                                        const stayFrom = toDate(item.from);
                                                        const agreementEnd = toDate(item.to);
                                                        const yearStart = new Date(month.getFullYear(), 0, 1);
                                                        const yearEnd = new Date(month.getFullYear(), 11, 31);

                                                        // Include stays that overlap the year, or expired
                                                        // stays that remain pending renewal.
                                                        return (
                                                            (stayFrom <= yearEnd && agreementEnd >= yearStart) ||
                                                            (
                                                                stayFrom <= yearEnd &&
                                                                isAgreementExpired(item)
                                                            )
                                                        );
                                                    })
                                                    : periodStays;

                                                const yearTooltipStays = yearStays.filter((item) => {
                                                    const agreementStart = toDate(item.from);
                                                    const agreementEnd = toDate(item.to);

                                                    const nextStay = bed.stays
                                                        .filter((next) => toDate(next.from) > agreementStart)
                                                        .sort((a, b) => toDate(a.from) - toDate(b.from))[0];

                                                    const nextStayStart = nextStay ? toDate(nextStay.from) : null;

                                                    // An expired agreement remains visible until the next stay starts.
                                                    const effectiveEnd = isExpiredOnDate(item, periodEnd)
                                                        ? periodEnd
                                                        : agreementEnd;

                                                    let visibleEnd = effectiveEnd;

                                                    if (nextStayStart) {
                                                        const dayBeforeNextStay = new Date(nextStayStart);
                                                        dayBeforeNextStay.setDate(
                                                            dayBeforeNextStay.getDate() - 1
                                                        );

                                                        visibleEnd = new Date(
                                                            Math.min(
                                                                visibleEnd.getTime(),
                                                                dayBeforeNextStay.getTime()
                                                            )
                                                        );
                                                    }

                                                    return (
                                                        agreementStart <= periodEnd &&
                                                        visibleEnd >= periodStart &&
                                                        visibleEnd >= agreementStart
                                                    );
                                                });

                                                const segments = yearStays
                                                    .map((item) => {
                                                        const monthStart = periodStart.getTime();
                                                        const monthEnd = periodEnd.getTime();

                                                        const stayStart = Math.max(
                                                            toDate(item.from).getTime(),
                                                            monthStart
                                                        );

                                                        const stayEnd = Math.min(
                                                            toDate(item.to).getTime(),
                                                            monthEnd
                                                        );

                                                        const totalDays =
                                                            Math.round((monthEnd - monthStart) / 86400000) + 1;

                                                        const startDay =
                                                            Math.round((stayStart - monthStart) / 86400000);

                                                        const duration =
                                                            Math.round((stayEnd - stayStart) / 86400000) + 1;

                                                        return {
                                                            item,
                                                            left: (startDay / totalDays) * 100,
                                                            width: (duration / totalDays) * 100,
                                                            startDay,
                                                            endDay: startDay + duration - 1,
                                                        };
                                                    })
                                                    .sort((a, b) => a.startDay - b.startDay);

                                                const hasMultipleTenants = periodStays.length > 1;

                                                const trackEnds = [];

                                                segments.forEach((segment) => {
                                                    let track = trackEnds.findIndex(
                                                        (endDay) => endDay < segment.startDay
                                                    );

                                                    if (track === -1) track = trackEnds.length;

                                                    trackEnds[track] = segment.endDay;
                                                    segment.track = track;
                                                });

                                                {/* End for Year */ }


                                                return (
                                                    <td key={period.value} className={`py-3 text-center ${timelineView === "year"
                                                        ? "min-w-[80px] px-0.5"
                                                        : "min-w-[31px] w-[31px] px-0.5"
                                                        }`}>

                                                        <div className="group relative mx-auto w-full">
                                                            {timelineView === "year" ? (
                                                                <div className="group relative mx-auto w-full">
                                                                    <div
                                                                        className="relative h-6 w-full overflow-hidden rounded-[3px] bg-slate-200"
                                                                    >
                                                                        {/* Occupied periods */}
                                                                        {/* {segments.map((segment, index) => (
                                                                            <div
                                                                                key={`${segment.item.occupancyId ?? segment.item.tenantId ?? segment.item.from}-${index}`}
                                                                                title={`${segment.item.tenant}: ${formatDate(toDate(segment.item.from))} – ${formatDate(toDate(segment.item.to))}`}
                                                                                //className="absolute inset-y-0 bg-emerald-400"
                                                                                className={`absolute inset-y-0 ${isExpiredOnDate(segment.item, periodEnd)
                                                                                    ? "bg-red-600"
                                                                                    : "bg-emerald-400"
                                                                                    }`}
                                                                                style={{
                                                                                    left: `${segment.left}%`,
                                                                                    width: `${segment.width}%`,
                                                                                    minWidth: "2px",
                                                                                }}
                                                                            />
                                                                        ))} */}
                                                                        {yearStays.map((item, index) => {
                                                                            const agreementStart = toDate(item.from);
                                                                            const agreementEnd = toDate(item.to);

                                                                            const nextStay = bed.stays
                                                                                .filter((next) => toDate(next.from) > agreementStart)
                                                                                .sort((a, b) => toDate(a.from) - toDate(b.from))[0];

                                                                            const nextStayStart = nextStay ? toDate(nextStay.from) : null;

                                                                            // const expired = isAgreementExpired(item);

                                                                            const expired = isExpiredOnDate(item, periodEnd);

                                                                            let visibleEnd =
                                                                                expired && periodEnd > agreementEnd
                                                                                    ? periodEnd.getTime()
                                                                                    : Math.min(
                                                                                        agreementEnd.getTime(),
                                                                                        periodEnd.getTime()
                                                                                    );

                                                                            const visibleStart = Math.max(
                                                                                agreementStart.getTime(),
                                                                                periodStart.getTime()
                                                                            );

                                                                            // Stop the previous agreement before the next tenant starts.
                                                                            if (nextStayStart) {
                                                                                const dayBeforeNextStay = new Date(nextStayStart);
                                                                                dayBeforeNextStay.setDate(
                                                                                    dayBeforeNextStay.getDate() - 1
                                                                                );

                                                                                visibleEnd = Math.min(
                                                                                    visibleEnd,
                                                                                    dayBeforeNextStay.getTime()
                                                                                );
                                                                            }

                                                                            if (visibleStart > periodEnd.getTime()) return null;
                                                                            if (visibleEnd < visibleStart) return null;

                                                                            const totalDays =
                                                                                Math.round(
                                                                                    (periodEnd.getTime() - periodStart.getTime()) / 86400000
                                                                                ) + 1;

                                                                            const left =
                                                                                (Math.round(
                                                                                    (visibleStart - periodStart.getTime()) / 86400000
                                                                                ) / totalDays) * 100;

                                                                            const width =
                                                                                ((Math.round(
                                                                                    (visibleEnd - visibleStart) / 86400000
                                                                                ) + 1) / totalDays) * 100;

                                                                            return (
                                                                                <div
                                                                                    key={`${item.occupancyId ?? item.tenantId ?? item.from}-${index}`}
                                                                                    title={`${item.tenant}: ${formatDate(agreementStart)} – ${formatDate(agreementEnd)}`}
                                                                                    className={`absolute inset-y-0 ${expired ? "bg-red-300" : "bg-emerald-400"
                                                                                        }`}
                                                                                    style={{
                                                                                        left: `${left}%`,
                                                                                        width: `${width}%`,
                                                                                        minWidth: "2px",
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })}

                                                                        {/* Indicator for multiple tenants in the month */}
                                                                        {hasMultipleTenants && (
                                                                            <div
                                                                                className="pointer-events-none absolute bottom-0 top-0 z-10 w-[2px] bg-slate-600"
                                                                                style={{
                                                                                    left: `${segments[1]?.left ?? 50}%`,
                                                                                }}
                                                                                title="New tenant joined"
                                                                            />
                                                                        )}
                                                                    </div>

                                                                    {/* Tooltip start */}
                                                                    {yearTooltipStays.length > 0 && (
                                                                        <div
                                                                            className={`pointer-events-none absolute left-1/2 z-[100] hidden w-max min-w-[220px] max-w-[280px] -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200/80 bg-white text-left text-xs text-slate-600 shadow-[0_12px_35px_-8px_rgba(15,23,42,0.22)] ring-1 ring-black/[0.02] group-hover:block ${bedIndex >= filteredBeds.length - 3
                                                                                ? "bottom-full mb-2"
                                                                                : "top-full mt-2"
                                                                                }`}
                                                                        >
                                                                            {/* Header */}
                                                                            <div className="border-b border-slate-100 bg-slate-50/80 px-3 py-2.5">
                                                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                                                    {period.label} {month.getFullYear()}
                                                                                </div>

                                                                                <div className="mt-1 font-semibold text-slate-800">
                                                                                    Room {bed.room} · {bed.bed}
                                                                                </div>
                                                                            </div>

                                                                            {/* Tenant details */}
                                                                            <div className="space-y-3 px-3 py-3">
                                                                                {yearTooltipStays.map((item, index) => (
                                                                                    <div
                                                                                        key={`${item.occupancyId ?? item.tenantId ?? item.from}-${index}`}
                                                                                        className={index > 0 ? "border-t border-slate-100 pt-3" : ""}
                                                                                    >
                                                                                        <div className="mb-2 font-semibold text-slate-900">
                                                                                            {item.tenant}
                                                                                        </div>

                                                                                        {item.from && (
                                                                                            <div className="flex justify-between gap-4">
                                                                                                <span className="text-slate-400">Move-in</span>
                                                                                                <span className="text-right font-medium text-slate-700">
                                                                                                    {formatDate(toDate(item.from))}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}

                                                                                        {item.to && (
                                                                                            <div className="mt-1 flex justify-between gap-4">
                                                                                                <span className={
                                                                                                    isExpiredOnDate(item, periodEnd)
                                                                                                        ? "font-semibold text-red-600"
                                                                                                        : "text-slate-400"
                                                                                                }>
                                                                                                    {isExpiredOnDate(item, periodEnd)
                                                                                                        ? "Renewal Pending"
                                                                                                        : isAgreementEnd(item)
                                                                                                            ? "Agreement End"
                                                                                                            : "Move-out"}
                                                                                                </span>
                                                                                                <span className="text-right font-medium text-slate-700">
                                                                                                    {formatDate(toDate(item.to))}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}

                                                                                        {(item.rent != null || item.monthlyRent != null) && (
                                                                                            <div className="mt-2 flex items-center justify-between gap-4 border-t border-slate-100 pt-2">
                                                                                                <span className="text-slate-500">Monthly rent</span>
                                                                                                <span className="font-semibold text-slate-900">
                                                                                                    ₹{Number(item.rent ?? item.monthlyRent).toLocaleString("en-IN")}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {/* Tooltip End */}
                                                                </div>
                                                            ) : (

                                                                <div className="group/day relative h-5 w-full min-w-[17px]">
                                                                    <div
                                                                        className={`h-5 w-full cursor-pointer rounded-[3px] ${COLORS[status]}`}
                                                                    />

                                                                    <div
                                                                        className={`pointer-events-none absolute left-1/2 z-[100] hidden w-max min-w-[210px] max-w-[260px] -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200/80 bg-white text-left text-xs text-slate-600 shadow-[0_12px_35px_-8px_rgba(15,23,42,0.22)] ring-1 ring-black/[0.02] group-hover/day:block ${bedIndex >= filteredBeds.length - 3
                                                                            ? "bottom-full mb-2"
                                                                            : "top-full mt-2"
                                                                            }`}
                                                                    >
                                                                        <div className="border-b border-slate-100 bg-slate-50/80 px-3 py-2.5">
                                                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                                                                {formatDate(periodStart)}
                                                                            </div>

                                                                            <div className="mt-1.5 flex items-center gap-2">
                                                                                <span
                                                                                    className={`h-2 w-2 rounded-full ${status === "occupied"
                                                                                        ? "bg-emerald-500"
                                                                                        : status === "vacant"
                                                                                            ? "bg-slate-400"
                                                                                            : status === "checkin"
                                                                                                ? "bg-blue-500"
                                                                                                : status === "checkout"
                                                                                                    ? "bg-rose-500"
                                                                                                    : status === "agreementEnd"
                                                                                                        ? "bg-violet-500"
                                                                                                        : status === "agreementExpired"
                                                                                                            ? "bg-red-600"
                                                                                                            : "bg-amber-500"
                                                                                        }`}
                                                                                />

                                                                                <span className="font-medium text-slate-800">
                                                                                    {STATUS_LABELS[status] || status}
                                                                                </span>
                                                                            </div>
                                                                        </div>


                                                                        {/* {(stay || expiredStay) && (

                                                                            <div className="space-y-2.5 px-3 py-3">
                                                                                <div>
                                                                                    <span className="text-gray-500">Tenant: </span>
                                                                                    <span className="font-medium text-gray-900">
                                                                                        {stay.tenant}
                                                                                    </span>
                                                                                </div>

                                                                                {stay.from && (
                                                                                    <div>
                                                                                        <span className="text-gray-500">Move-in: </span>
                                                                                        {formatDate(toDate(stay.from))}
                                                                                    </div>
                                                                                )}

                                                                                {stay.to && (
                                                                                    <div>
                                                                                        <span className={
                                                                                            isAgreementExpiredPending(stay)
                                                                                                ? "font-semibold text-red-600"
                                                                                                : "text-gray-500"
                                                                                        }>
                                                                                            {isAgreementExpiredPending(stay)
                                                                                                ? "Agreement Expired - Renewal Pending:"
                                                                                                : isAgreementEnd(stay)
                                                                                                    ? "Agreement End:"
                                                                                                    : "Move-out:"}
                                                                                        </span>
                                                                                        {formatDate(toDate(stay.to))}
                                                                                    </div>
                                                                                )}

                                                                                {(stay.rent != null || stay.monthlyRent != null) && (
                                                                                    <div>
                                                                                        <span className="text-gray-500">Rent: </span>
                                                                                        <span className="font-medium text-gray-900">
                                                                                            ₹{Number(stay.rent ?? stay.monthlyRent).toLocaleString("en-IN")}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )} */}
                                                                        {(stay || expiredStay) && (() => {
                                                                            const tooltipStay = stay || expiredStay;

                                                                            return (
                                                                                <div className="space-y-2.5 px-3 py-3">
                                                                                    <div>
                                                                                        <span className="text-gray-500">Tenant: </span>
                                                                                        <span className="font-medium text-gray-900">
                                                                                            {truncateText(tooltipStay?.tenant, 20)}
                                                                                        </span>
                                                                                    </div>

                                                                                    {tooltipStay.from && (
                                                                                        <div>
                                                                                            <span className="text-gray-500">Move-in: </span>
                                                                                            {formatDate(toDate(tooltipStay.from))}
                                                                                        </div>
                                                                                    )}

                                                                                    {tooltipStay.to && (
                                                                                        <div>
                                                                                            <span
                                                                                                className={`font-semibold ${tooltipStay.isEvicted === 0 &&
                                                                                                    tooltipStay.status !== 4 &&
                                                                                                    isExpiredOnDate(tooltipStay, periodStart)
                                                                                                    ? "text-red-600"
                                                                                                    : "text-gray-500"
                                                                                                    }`}
                                                                                            >
                                                                                                {tooltipStay.isEvicted !== 0 || tooltipStay.status === 4
                                                                                                    ? "Moved Out:"
                                                                                                    : isExpiredOnDate(tooltipStay, periodStart)
                                                                                                        ? "Renewal Pending:"
                                                                                                        : "Agreement End:"}
                                                                                            </span>{" "}
                                                                                            {formatDate(toDate(tooltipStay.to))}
                                                                                        </div>
                                                                                    )}

                                                                                    {(tooltipStay.rent != null ||
                                                                                        tooltipStay.monthlyRent != null) && (
                                                                                            <div>
                                                                                                <span className="text-gray-500">Rent: </span>
                                                                                                <span className="font-medium text-gray-900">
                                                                                                    ₹{Number(
                                                                                                        tooltipStay.rent ?? tooltipStay.monthlyRent
                                                                                                    ).toLocaleString("en-IN")}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            );
                                                                        })()}

                                                                    </div>
                                                                </div>

                                                            )}
                                                        </div>

                                                    </td>
                                                );
                                            })}

                                            <td className="border-l border-gray-100 px-2 text-center font-medium text-gray-700">
                                                {bed.metrics.occupiedDays}
                                            </td>
                                            <td className="px-2 text-center text-gray-500">
                                                {bed.metrics.vacantDays}
                                            </td>
                                            <td className="px-2 text-center">
                                                <span
                                                    className={`inline-flex min-w-[43px] justify-center rounded-md px-2 py-1 font-semibold ${bed.metrics.occupancy >= 80
                                                        ? "bg-emerald-50 text-emerald-700"
                                                        : bed.metrics.occupancy >= 50
                                                            ? "bg-amber-50 text-amber-700"
                                                            : "bg-rose-50 text-rose-600"
                                                        }`}
                                                >
                                                    {bed.metrics.occupancy}%
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {!loading && filteredBeds.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={timelinePeriods.length + 4}
                                            className="px-4 py-12 text-center text-sm text-gray-400"
                                        >
                                            No beds found for this selection.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs text-gray-400">
                        <span>
                            Showing {filteredBeds.length} of {beds.length} beds
                        </span>
                        <span>{timelineView === "year"
                            ? month.getFullYear()
                            : formatMonth(month)}</span>
                    </div>
                </div>

                {/* Selected bed details */}
                {/* {selectedBed && (
                        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">
                                        Floor {selectedBed.floor} · Room{" "}
                                        {selectedBed.room} · {selectedBed.bed}
                                    </h3>
                                    <p className="mt-1 text-xs text-gray-400">
                                        Occupancy details for {formatMonth(month)}
                                    </p>
                                </div>

                                <span
                                    className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                                        selectedBed.metrics.occupancy >= 80
                                            ? "bg-emerald-50 text-emerald-700"
                                            : selectedBed.metrics.occupancy >= 50
                                              ? "bg-amber-50 text-amber-700"
                                              : "bg-rose-50 text-rose-600"
                                    }`}
                                >
                                    {selectedBed.metrics.occupancy}% Occupancy
                                </span>
                            </div>

                            <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-[0.8fr_1.5fr]">
                                <div className="rounded-md border border-gray-100 p-4">
                                    <div className="mb-4 text-sm font-semibold text-gray-700">
                                        Monthly Summary
                                    </div>

                                    <div className="mb-4 flex items-center gap-4">
                                        <div
                                            className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
                                            style={{
                                                background: `conic-gradient(#10b981 ${selectedBed.metrics.occupancy}%, #e2e8f0 0)`,
                                            }}
                                        >
                                            <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-white text-lg font-semibold text-gray-800">
                                                {selectedBed.metrics.occupancy}%
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div>
                                                <div className="text-xl font-semibold text-gray-800">
                                                    {selectedBed.metrics.occupiedDays}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Occupied Days
                                                </div>
                                            </div>

                                            <div>
                                                <div className="text-xl font-semibold text-gray-800">
                                                    {selectedBed.metrics.vacantDays}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Vacant Days
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {selectedBed.metrics.blockedDays > 0 && (
                                        <div className="border-t border-gray-100 pt-3 text-xs text-gray-500">
                                            Maintenance / Blocked Days:{" "}
                                            <span className="font-semibold text-gray-700">
                                                {selectedBed.metrics.blockedDays}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="overflow-hidden rounded-md border border-gray-100">
                                    <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
                                        Stay Periods
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gray-50 text-gray-500">
                                                <tr>
                                                    <th className="px-3 py-3 text-left font-medium">
                                                        Status
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-medium">
                                                        From
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-medium">
                                                        To
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-medium">
                                                        Days
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-medium">
                                                        Tenant
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {selectedStays.map(
                                                    (stay, index) => (
                                                        <tr
                                                            key={`${stay.from}-${index}`}
                                                            className="border-t border-gray-100"
                                                        >
                                                            <td className="px-3 py-3">
                                                                <span
                                                                    className={`rounded px-2 py-1 ${
                                                                        stay.status === "Occupied"
                                                                            ? "bg-emerald-50 text-emerald-700"
                                                                            : "bg-slate-100 text-slate-600"
                                                                    }`}
                                                                >
                                                                    {stay.status}
                                                                </span>
                                                            </td>

                                                            <td className="whitespace-nowrap px-3 py-3 text-gray-600">
                                                                {formatDate(
                                                                    stay.fromDate
                                                                )}
                                                            </td>

                                                            <td className="whitespace-nowrap px-3 py-3 text-gray-600">
                                                                {formatDate(
                                                                    stay.toDate
                                                                )}
                                                            </td>

                                                            <td className="px-3 py-3 font-medium text-gray-700">
                                                                {stay.days}
                                                            </td>

                                                            <td className="whitespace-nowrap px-3 py-3 text-gray-600">
                                                                {stay.tenant}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )} */}
            </div>
        </BoxContainer>
    );

}

const SegmentButton = ({ active, count, children, ...props }) => (
    <button
        {...props}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-[transform,box-shadow] duration-150 ease-out
      ${active
                ? "bg-cyan-50 text-primary-color shadow-sm scale-[1.02]"
                : "text-gray-600 hover:bg-gray-50 hover:scale-[1.01]"
            }`}
    >
        <span>{children}</span>

        {typeof count === "number" && (
            <span
                className={`min-w-[20px] h-5 px-2 rounded-full text-xs flex items-center justify-center
          ${active ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}
        `}
            >
                {count}
            </span>
        )}
    </button>
);

function SummaryCard({ icon, label, value, detail, iconClass }) {
    return (
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg ${iconClass}`}
            >
                {icon}
            </div>

            <div className="min-w-0">
                <div className="truncate text-xl font-semibold text-gray-900">
                    {value}
                </div>
                <div className="mt-0.5 text-xs text-gray-500">{label}</div>
                {detail && (
                    <div className="mt-1 text-[10px] text-gray-400">
                        {detail}
                    </div>
                )}
            </div>
        </div>
    );
}



export default BedsCalendar;